"use client";

import { usePathname, useRouter } from "next/navigation";
import {
  createContext,
  type ReactNode,
  startTransition,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { localizePublicPathname } from "@/lib/i18n/public-routing";
import {
  LANGUAGE_FLAG_MAP,
  LANGUAGE_METADATA,
  LANGUAGE_NAME_MAP,
  LANGUAGE_NATIVE_NAME_MAP,
  type LanguageDirection,
  type LanguageMetadata,
  RTL_LANGUAGES,
  type SupportedLanguage,
} from "@/lib/languages";
import {
  reportWarning,
  suppressErrorReporting,
} from "@/lib/observability/report-error";

const LANGUAGE_COOKIE_NAME = "vidseeds_locale";

interface LanguageContextType {
  /** Current active language code */
  language: SupportedLanguage;

  /** Change the active language */
  setLanguage: (
    lang: SupportedLanguage,
    options?: { skipDbWrite?: boolean },
  ) => Promise<void>;

  /** Is currently changing language */
  isChanging: boolean;

  /** Language direction (ltr/rtl) */
  direction: LanguageDirection;

  /** Whether the current language is RTL */
  isRTL: boolean;

  /** Get language name in English */
  getName: (code?: SupportedLanguage) => string;

  /** Get language native name */
  getNativeName: (code?: SupportedLanguage) => string;

  /** Get language flag emoji */
  getFlag: (code?: SupportedLanguage) => string;

  /** Get full language metadata */
  getMetadata: (code?: SupportedLanguage) => LanguageMetadata | undefined;

  /** All available languages */
  availableLanguages: readonly LanguageMetadata[];
}

const LanguageContext = createContext<LanguageContextType | null>(null);

function syncDocumentLanguage(lang: SupportedLanguage) {
  if (typeof document !== "undefined") {
    const dir = RTL_LANGUAGES.has(lang) ? "rtl" : "ltr";
    document.documentElement.lang = lang;
    document.documentElement.dir = dir;
  }
}

function persistLanguagePreference(lang: SupportedLanguage) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(LANGUAGE_COOKIE_NAME, lang);
  } catch (_err) {
    suppressErrorReporting(
      "localStorage access denied in private/incognito mode",
      {
        source: "contexts.LanguageContext",
        feature: "i18n",
        action: "persistLanguagePreference",
      },
    );
  }
  document.cookie = `${LANGUAGE_COOKIE_NAME}=${lang}; Max-Age=${60 * 60 * 24 * 365}; Path=/; SameSite=Lax`;
}

export function LanguageProvider({
  children,
  initialLanguage = "en",
  publicRouteLocked = false,
  localeIsExplicit = false,
}: {
  children: ReactNode;
  initialLanguage?: SupportedLanguage;
  publicRouteLocked?: boolean;
  /** Whether the locale came from an explicit source (DB/cookie) vs auto-detected (Accept-Language). */
  localeIsExplicit?: boolean;
}) {
  const [language, setLanguageState] =
    useState<SupportedLanguage>(initialLanguage);
  const [isChanging, setIsChanging] = useState(false);
  const router = useRouter();
  const pathname = usePathname();

  // Trust server-provided `initialLanguage` (from RootLayout's
  // `detectLocaleWithSource()`: DB preferred_language → default "en").
  // On mount, if client-side storage disagrees with the server value,
  // clear the stale client state to prevent future confusion — but
  // ALWAYS render what the server says (single source of truth).
  useEffect(() => {
    const initial = initialLanguage;

    if (typeof window !== "undefined" && !publicRouteLocked) {
      const storedCookie = document.cookie
        .split("; ")
        .find((row) => row.startsWith(`${LANGUAGE_COOKIE_NAME}=`))
        ?.split("=")[1];
      let storedLocal: string | null = null;
      try {
        storedLocal = localStorage.getItem(LANGUAGE_COOKIE_NAME);
      } catch (_err) {
        suppressErrorReporting(
          "localStorage access denied in private/incognito mode",
          {
            source: "contexts.LanguageContext",
            feature: "i18n",
            action: "readStoredLocale",
          },
        );
      }
      const stale = storedCookie || storedLocal;

      if (stale && stale !== initial) {
        console.debug(
          `[LanguageContext] Clearing stale locale: stored=${stale}, server=${initial}`,
        );
        try {
          localStorage.removeItem(LANGUAGE_COOKIE_NAME);
          document.cookie = `${LANGUAGE_COOKIE_NAME}=; Max-Age=0; Path=/; SameSite=Lax`;
        } catch (err) {
          // Best-effort cleanup - storage access may be denied in private mode
          console.error("[LanguageContext] Failed to clear stale locale:", err);
          reportWarning(err, {
            source: "contexts.LanguageContext",
            feature: "i18n",
            action: "clearStaleLocale",
          });
        }
      }
    }

    setLanguageState((current) => (current === initial ? current : initial));
    syncDocumentLanguage(initial);
  }, [initialLanguage, publicRouteLocked, localeIsExplicit]);

  const setLanguage = useCallback(
    async (lang: SupportedLanguage, options?: { skipDbWrite?: boolean }) => {
      if (lang === language) return;

      setIsChanging(true);

      try {
        setLanguageState(lang);
        syncDocumentLanguage(lang);
        persistLanguagePreference(lang);

        // AWAIT the DB write so the server sees the new value before we
        // trigger router.refresh(). Previous fire-and-forget caused a race
        // where refresh read stale DB/cache ("ar") before PATCH arrived.
        if (!options?.skipDbWrite) {
          try {
            const res = await fetch("/api/user/preferred-language/", {
              method: "PATCH",
              headers: { "Content-Type": "application/json" },
              credentials: "include",
              body: JSON.stringify({ preferredLanguage: lang }),
            });
            if (res.status === 401) {
              console.info(
                "[LanguageContext] Skipping DB language sync — session not authenticated (preference kept locally)",
              );
            } else if (!res.ok) {
              const snippet = await res.text().catch(() => "");
              console.warn(
                "[LanguageContext] preferred-language PATCH failed",
                res.status,
                snippet.slice(0, 300),
              );
              reportWarning(
                new Error(`preferred-language PATCH ${res.status}`),
                {
                  source: "contexts.LanguageContext",
                  feature: "i18n",
                  action: "syncLanguageToDB",
                  extra: {
                    httpStatus: res.status,
                    responseSnippet: snippet.slice(0, 500),
                  },
                },
              );
            }
          } catch (err) {
            console.error(
              "[LanguageContext] Failed to sync language to DB:",
              err,
            );
            reportWarning(err, {
              source: "contexts.LanguageContext",
              feature: "i18n",
              action: "syncLanguageToDB",
            });
          }
        }

        if (publicRouteLocked) {
          const targetPath = pathname
            ? localizePublicPathname(pathname, lang)
            : null;
          const query =
            typeof window !== "undefined"
              ? new URLSearchParams(window.location.search).toString()
              : "";
          const hash =
            typeof window !== "undefined" ? window.location.hash : "";
          const href = targetPath
            ? `${targetPath}${query ? `?${query}` : ""}${hash}`
            : null;

          if (href) {
            startTransition(() => {
              router.push(href);
            });
          } else {
            router.refresh();
          }
        } else {
          router.refresh();
        }
      } finally {
        setTimeout(() => setIsChanging(false), 500);
      }
    },
    [language, pathname, publicRouteLocked, router],
  );

  const isRTL = useMemo(() => RTL_LANGUAGES.has(language), [language]);
  const direction = useMemo<LanguageDirection>(
    () => (isRTL ? "rtl" : "ltr"),
    [isRTL],
  );

  const getName = useCallback(
    (code?: SupportedLanguage) => {
      return LANGUAGE_NAME_MAP[code || language] || code || language;
    },
    [language],
  );

  const getNativeName = useCallback(
    (code?: SupportedLanguage) => {
      return LANGUAGE_NATIVE_NAME_MAP[code || language] || code || language;
    },
    [language],
  );

  const getFlag = useCallback(
    (code?: SupportedLanguage) => {
      return LANGUAGE_FLAG_MAP[code || language] || "🌐";
    },
    [language],
  );

  const getMetadata = useCallback(
    (code?: SupportedLanguage) => {
      return LANGUAGE_METADATA.find((l) => l.code === (code || language));
    },
    [language],
  );

  const value = useMemo<LanguageContextType>(
    () => ({
      language,
      setLanguage,
      isChanging,
      direction,
      isRTL,
      getName,
      getNativeName,
      getFlag,
      getMetadata,
      availableLanguages: LANGUAGE_METADATA,
    }),
    [
      language,
      setLanguage,
      isChanging,
      direction,
      isRTL,
      getName,
      getNativeName,
      getFlag,
      getMetadata,
    ],
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage(): LanguageContextType {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
}

export function useLanguageSafe(): LanguageContextType | null {
  return useContext(LanguageContext);
}

// Re-export types for convenience
export type { LanguageDirection, LanguageMetadata, SupportedLanguage };
